import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Search, MapPin, Briefcase, DollarSign, Calendar, FileText, Loader2, AlertCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/SEO';
import JobApplicationModal from '../components/JobApplicationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Candidates = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/jobs`);
      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }
      const data = await response.json();
      setJobs(data);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Unable to load job listings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setIsApplicationModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsApplicationModalOpen(false);
    setSelectedJob(null);
  };

  // Extract unique locations for filter
  const locations = [...new Set(jobs.map(job => job.location).filter(Boolean))];
  
  // Extract unique employment types for filter
  const employmentTypes = [...new Set(jobs.map(job => job.employment_type).filter(Boolean))];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || job.employment_type === selectedType;
    const matchesLocation = selectedLocation === 'all' || job.location?.includes(selectedLocation);
    
    return matchesSearch && matchesType && matchesLocation;
  });

  const handleApply = (jobTitle) => {
    toast.success(`Interest registered for ${jobTitle}!`, {
      description: 'A recruiter will contact you within 24-48 hours.'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Recently';
    }
  };

  const formatEmploymentType = (type) => {
    if (!type) return 'Full-time';
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('-');
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="For Candidates"
        description="Find your next career opportunity with Omnia Paratus Talent Solutions. We connect talented professionals with top employers in accounting, healthcare, logistics, and HR & people compliance industries across Massachusetts and nationwide."
        keywords="find jobs, career opportunities, job search, accounting jobs, healthcare careers, logistics positions, HR jobs, people compliance jobs, Massachusetts jobs, professional employment"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Discover Your Next Career Move
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-10">
            Explore opportunities with employers who value your skills and ambitions
          </p>
        </div>
      </section>

      {/* Search Filters */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200 sticky top-20 z-40 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by job title, company, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="job-search-input"
                />
              </div>
            </div>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger data-testid="employment-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {employmentTypes.map(type => (
                  <SelectItem key={type} value={type}>{formatEmploymentType(type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="location-filter">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            {loading ? 'Loading...' : `Showing ${filteredJobs.length} of ${jobs.length} opportunities`}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-12 w-12 text-[#D4AF37] animate-spin mb-4" />
              <p className="text-gray-600">Loading job opportunities...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <Card className="border-none shadow-lg">
              <CardContent className="p-12 text-center">
                <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-700 mb-2">Unable to Load Jobs</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <Button onClick={fetchJobs} variant="outline">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Job Cards */}
          {!loading && !error && (
            <div className="space-y-6">
              {filteredJobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">{job.title}</h3>
                            <p className="text-lg text-gray-700 font-medium">{job.company}</p>
                          </div>
                          <span className="px-4 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-semibold whitespace-nowrap">
                            {formatEmploymentType(job.employment_type)}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-4 mb-4 text-gray-600">
                          {job.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-[#D4AF37]" />
                              <span className="text-sm">{job.location}</span>
                            </div>
                          )}
                          {job.salary && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-[#D4AF37]" />
                              <span className="text-sm">{job.salary}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-[#D4AF37]" />
                            <span className="text-sm">Posted {formatDate(job.date_posted)}</span>
                          </div>
                        </div>

                        {job.description && (
                          <p className="text-gray-700 mb-4 leading-relaxed line-clamp-3">{job.description}</p>
                        )}

                        {job.requirements && job.requirements.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-[#0A1F44] mb-2">Requirements:</h4>
                            <ul className="space-y-1">
                              {job.requirements.slice(0, 4).map((req, idx) => (
                                <li key={idx} className="text-gray-600 text-sm flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full flex-shrink-0"></span>
                                  {req}
                                </li>
                              ))}
                              {job.requirements.length > 4 && (
                                <li className="text-gray-500 text-sm italic">
                                  + {job.requirements.length - 4} more requirements
                                </li>
                              )}
                            </ul>
                          </div>
                        )}

                        {/* Posted boards badges */}
                        {job.boards && job.boards.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {job.boards.map((board, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                              >
                                {board}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="lg:w-48 flex flex-col gap-3">
                        <Button
                          onClick={() => handleApplyClick(job)}
                          className="w-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432]"
                          data-testid={`apply-btn-${job.id}`}
                        >
                          Apply Now
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          className="w-full border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white"
                        >
                          <Link to="/contact?tab=candidate">Contact Recruiter</Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {filteredJobs.length === 0 && jobs.length > 0 && (
                <Card className="border-none shadow-lg">
                  <CardContent className="p-12 text-center">
                    <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Jobs Found</h3>
                    <p className="text-gray-600 mb-6">
                      Try adjusting your search filters to see more opportunities
                    </p>
                    <Button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedType('all');
                        setSelectedLocation('all');
                      }}
                      variant="outline"
                    >
                      Clear Filters
                    </Button>
                  </CardContent>
                </Card>
              )}

              {jobs.length === 0 && !loading && !error && (
                <Card className="border-none shadow-lg">
                  <CardContent className="p-12 text-center">
                    <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">No Open Positions</h3>
                    <p className="text-gray-600 mb-6">
                      We're currently building our job listings. Submit your resume and we'll reach out when opportunities match your profile.
                    </p>
                    <Button
                      asChild
                      className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432]"
                    >
                      <Link to="/contact?tab=candidate">Submit Your Resume</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Why Work with Omnia Paratus Talent Solutions?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're committed to helping you find not just a job, but the right career move
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Personalized Guidance',
                description: 'One-on-one support throughout your job search journey, from application to offer negotiation.'
              },
              {
                title: 'Exclusive Opportunities',
                description: 'Access to positions not advertised publicly and direct connections with hiring managers.'
              },
              {
                title: 'Career Advocacy',
                description: 'We represent you professionally and highlight your strengths to potential employers.'
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-2 border-[#D4AF37] shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#0A1F44] rounded-2xl flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#D4AF37]">{index + 1}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Resume CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c]">
        <div className="max-w-4xl mx-auto text-center">
          <FileText className="h-16 w-16 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            Submit your resume and we'll reach out when opportunities matching your profile become available.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] text-lg px-8 py-6 shadow-xl"
          >
            <Link to="/contact?tab=candidate" className="flex items-center gap-2">
              Submit Resume <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />

      {/* Job Application Modal */}
      <JobApplicationModal
        isOpen={isApplicationModalOpen}
        onClose={handleCloseModal}
        job={selectedJob}
      />
    </div>
  );
};

export default Candidates;
